import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Upload, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

// Раздел «Обновления»: заливка бандлов лаунчера и UI + раскатка.
// UI киоски подтягивают сами по manifest; лаунчер раскатывается агентам
// командой apply_update (применяется в простое/заставке, с откатом при сбое).

const CHANNELS = [
  {
    key: 'launcher',
    title: 'Лаунчер (.exe)',
    hint: 'ZIP содержимого папки publish (KioskLauncher.exe + dll + runtimes). Применяет агент в простое, с откатом при сбое.',
  },
  {
    key: 'ui',
    title: 'UI (интерфейс)',
    hint: 'ZIP папки ui/ БЕЗ config.json и locales.json. Киоск подменяет файлы сам в простое и перезагружает экран — без перезапуска.',
  },
];

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

const ChannelCard = ({ channel, info, onChanged }) => {
  const { relayUrl, token, fetchAuthorized } = useAuth();
  const [file, setFile]       = useState(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [status, setStatus]   = useState(null);

  const flash = (ok, text, ms = 6000) => {
    setStatus({ ok, text });
    setTimeout(() => setStatus(null), ms);
  };

  const upload = async () => {
    if (!file)    return flash(false, 'Выберите ZIP-файл');
    if (!version.trim()) return flash(false, 'Укажите версию (например 1.2.0)');
    setBusy(true);
    setStatus(null);
    try {
      const qs = new URLSearchParams({ version: version.trim(), notes });
      const res = await fetch(`${relayUrl}/api/dashboard/updates/${channel.key}?${qs}`, {
        method: 'PUT',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/zip' },
        body: file,
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) {
        flash(true,
          channel.key === 'launcher'
            ? `Опубликовано v${d.version}. Раскатано на ${d.rolledOut} киосков (применят в простое).`
            : `Опубликовано v${d.version}. Киоски подтянут UI сами в простое.`);
        setFile(null); setVersion(''); setNotes('');
        onChanged && onChanged();
      } else {
        flash(false, d.error || `Ошибка ${res.status}`);
      }
    } catch (e) {
      flash(false, 'Ошибка соединения');
    } finally {
      setBusy(false);
    }
  };

  const rollout = async () => {
    setBusy(true);
    try {
      const res = await fetchAuthorized(`/api/dashboard/updates/${channel.key}/rollout`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      flash(res.ok && d.success, d.note || (res.ok ? `Раскатано на ${d.rolledOut} киосков.` : (d.error || 'Ошибка')));
      onChanged && onChanged();
    } catch { flash(false, 'Ошибка соединения'); }
    finally { setBusy(false); }
  };

  const toggleEnabled = async () => {
    if (!info) return;
    setBusy(true);
    try {
      const res = await fetchAuthorized(`/api/dashboard/updates/${channel.key}/enabled`, {
        method: 'PUT', body: JSON.stringify({ enabled: !info.enabled }),
      });
      if (res.ok) onChanged && onChanged();
    } catch {} finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <p className="section-title" style={{ margin: 0 }}>{channel.title}</p>
        {info
          ? <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
              v<b style={{ color: 'var(--text)' }}>{info.version}</b> · {fmtSize(info.size)} ·{' '}
              на версии {info.adoption?.on_version ?? 0}/{info.adoption?.total ?? 0} киосков
            </span>
          : <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>не опубликовано</span>}
      </div>
      <p className="text-dim" style={{ fontSize: '12px', margin: '6px 0 16px' }}>{channel.hint}</p>

      {info && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', gap: '10px', alignItems: 'center', fontSize: '13px', cursor: busy ? 'not-allowed' : 'pointer', color: 'var(--text)', whiteSpace: 'nowrap' }}>
            <span className="ios-switch">
              <input type="checkbox" checked={!!info.enabled} onChange={toggleEnabled} disabled={busy} />
              <span className="ios-track"><span className="ios-knob" /></span>
            </span>
            <span>
              Авто-раскатка{' '}
              <span style={{ color: 'var(--text-muted)' }}>
                {channel.key === 'launcher' ? '(при заливке сразу команда агентам)' : '(пуш при заливке + дотяг по heartbeat)'}
              </span>
            </span>
          </label>
          {channel.key === 'launcher' && (
            <button className="btn" style={{ gap: '6px' }} onClick={rollout} disabled={busy}>
              <RefreshCw size={13} /> Раскатить заново
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '1 1 220px' }}>
          <label className="field-label">ZIP-бандл</label>
          <input type="file" accept=".zip,application/zip" onChange={e => setFile(e.target.files?.[0] || null)} />
        </div>
        <div className="field" style={{ flex: '0 0 120px' }}>
          <label className="field-label">Версия</label>
          <input type="text" value={version} placeholder="1.2.0" onChange={e => setVersion(e.target.value)} />
        </div>
        <div className="field" style={{ flex: '1 1 200px' }}>
          <label className="field-label">Заметка (необязательно)</label>
          <input type="text" value={notes} placeholder="что изменилось" onChange={e => setNotes(e.target.value)} />
        </div>
      </div>

      {status && (
        <div className={`alert ${status.ok ? 'alert-success' : 'alert-error'}`} style={{ margin: '14px 0 0' }}>
          {status.ok ? <CheckCircle size={14} style={{ flexShrink: 0 }} /> : <AlertCircle size={14} style={{ flexShrink: 0 }} />}
          <span>{status.text}</span>
        </div>
      )}

      <button className="btn btn-primary" style={{ marginTop: '14px', gap: '6px', padding: '10px 24px' }} onClick={upload} disabled={busy}>
        <Upload size={14} /> {busy ? 'Загрузка…' : 'Залить и опубликовать'}
      </button>
    </div>
  );
};

export const UpdatesSection = () => {
  const { fetchAuthorized } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchAuthorized('/api/dashboard/updates');
      if (res.ok) setData(await res.json());
    } catch {}
  }, [fetchAuthorized]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card" style={{ maxWidth: '800px', padding: '28px', marginBottom: '20px' }}>
      <p className="section-title" style={{ marginBottom: '6px' }}>Обновления</p>
      <p className="text-dim" style={{ fontSize: '12px', marginBottom: '18px' }}>
        Залей бандл один раз — киоски подтянут его сами и применят в простое (на заставке).
        Лаунчер обновляет агент-супервизор с откатом при сбое; UI киоск подменяет сам без перезапуска.
      </p>
      {CHANNELS.map(ch => (
        <ChannelCard key={ch.key} channel={ch} info={data?.[ch.key] || null} onChanged={load} />
      ))}
    </div>
  );
};
